import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  FiTrendingUp,
  FiLoader,
  FiRefreshCw,
  FiAlertCircle,
  FiEye,
  FiTarget,
  FiUsers,
  FiBarChart2,
  FiInfo,
  FiExternalLink,
  FiFileText,
} from 'react-icons/fi'
import { LuBanknote } from 'react-icons/lu'
import { ReportView } from './MetaAdsReport'

// Painel Meta Ads — consome /api/meta-ads/{overview,attribution,comparison}.
// Token + ad_account_id vêm do banco no backend (whatsapp_devices do device
// de Atendimento — mesmo usado pra disparar templates WhatsApp). Frontend
// não conhece nem precisa conhecer credentials.

type SubTab = 'overview' | 'attribution' | 'efficiency' | 'comparison' | 'report'
type DatePreset = 'last_7d' | 'last_30d' | 'last_90d'

interface OverviewCampaign {
  id: string; name: string; status: string; objective: string
  createdAt: string; dailyBudget: number | null
  spend: number; impressions: number; clicks: number
  ctr: number; cpm: number; cpc: number
  leads: number; cpl: number | null
}
interface OverviewResponse {
  success: boolean; adAccount: string; datePreset: string
  campaigns: OverviewCampaign[]
  totals: { spend: number; impressions: number; clicks: number; leads: number; cpl: number | null; ctr: number }
  error?: string; hint?: string; metaCode?: number
}

interface AttributionAd {
  adId: string; adName: string | null
  campaign: { id: string; name: string } | null
  adset: { id: string; name: string } | null
  leads: number; firstLead: string; lastLead: string
  spend: number | null; cpl: number | null
  error?: string
}
interface AttributionResponse {
  success: boolean; days: number
  ads: AttributionAd[]; note?: string; error?: string
}

interface EfficiencyAd {
  adId: string; adName: string | null; adsetName: string | null; campaignName: string | null
  spend: number; impressions: number; clicks: number; lastActive: string | null
  leads: number; conversions: number
  spendRecent: number; leadsRecent: number; conversionsRecent: number
  cpl: number | null; costPerConversion: number | null
  fatigued: boolean
}
interface EfficiencyResponse {
  success: boolean; since: string; until: string
  fatigueRule: { minLeads: number; windowDays: number }
  ads: EfficiencyAd[]
  totals: { spend: number; leads: number; conversions: number; cpl: number | null; costPerConversion: number | null }
  lastSyncAt: string | null
  note?: string; error?: string
}

interface ComparisonResponse {
  success: boolean; days: number
  matrix: Record<string, {
    _totalLeads: number
    templates: Record<string, { sent: number; delivered: number; read: number; failed: number }>
  }>
  note?: string; error?: string
}

const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtInt = (n: number) => n.toLocaleString('pt-BR')
const fmtPct = (n: number) => `${n.toFixed(2)}%`

export function MetaAdsPanel() {
  const { authenticatedFetch } = useAuth()

  const [subTab, setSubTab] = useState<SubTab>('overview')
  const [datePreset, setDatePreset] = useState<DatePreset>('last_30d')

  const [overview, setOverview] = useState<OverviewResponse | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [attribution, setAttribution] = useState<AttributionResponse | null>(null)
  const [attributionLoading, setAttributionLoading] = useState(false)
  const [comparison, setComparison] = useState<ComparisonResponse | null>(null)
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [efficiency, setEfficiency] = useState<EfficiencyResponse | null>(null)
  const [efficiencyLoading, setEfficiencyLoading] = useState(false)

  const presetToDays = (p: DatePreset) => p === 'last_7d' ? 7 : p === 'last_30d' ? 30 : 90

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true)
    try {
      const r = await authenticatedFetch(`/api/meta-ads/overview?date_preset=${datePreset}`)
      const d = await r.json()
      setOverview(d)
    } catch (e: any) {
      setOverview({ success: false, adAccount: '', datePreset, campaigns: [], totals: { spend: 0, impressions: 0, clicks: 0, leads: 0, cpl: null, ctr: 0 }, error: e?.message || 'Erro de rede' })
    } finally { setOverviewLoading(false) }
  }, [authenticatedFetch, datePreset])

  const loadAttribution = useCallback(async () => {
    setAttributionLoading(true)
    try {
      const r = await authenticatedFetch(`/api/meta-ads/attribution?days=${presetToDays(datePreset)}`)
      setAttribution(await r.json())
    } catch (e: any) {
      setAttribution({ success: false, days: presetToDays(datePreset), ads: [], error: e?.message || 'Erro de rede' })
    } finally { setAttributionLoading(false) }
  }, [authenticatedFetch, datePreset])

  const loadComparison = useCallback(async () => {
    setComparisonLoading(true)
    try {
      const r = await authenticatedFetch(`/api/meta-ads/comparison?days=${presetToDays(datePreset)}`)
      setComparison(await r.json())
    } catch (e: any) {
      setComparison({ success: false, days: presetToDays(datePreset), matrix: {}, error: e?.message || 'Erro de rede' })
    } finally { setComparisonLoading(false) }
  }, [authenticatedFetch, datePreset])

  const loadEfficiency = useCallback(async (resync = false) => {
    setEfficiencyLoading(true)
    try {
      const r = await authenticatedFetch(`/api/meta-ads/efficiency?days=${presetToDays(datePreset)}${resync ? '&resync=1' : ''}`)
      setEfficiency(await r.json())
    } catch (e: any) {
      setEfficiency({ success: false, since: '', until: '', fatigueRule: { minLeads: 0, windowDays: 0 }, ads: [], totals: { spend: 0, leads: 0, conversions: 0, cpl: null, costPerConversion: null }, lastSyncAt: null, error: e?.message || 'Erro de rede' })
    } finally { setEfficiencyLoading(false) }
  }, [authenticatedFetch, datePreset])

  // Carrega a view ativa quando ela aparece ou quando muda período.
  useEffect(() => {
    if (subTab === 'overview' && !overview) loadOverview()
    if (subTab === 'attribution' && !attribution) loadAttribution()
    if (subTab === 'efficiency' && !efficiency) loadEfficiency()
    if (subTab === 'comparison' && !comparison) loadComparison()
  }, [subTab, overview, attribution, efficiency, comparison, loadOverview, loadAttribution, loadEfficiency, loadComparison])

  // Período mudou → invalida tudo e recarrega o ativo.
  useEffect(() => {
    setOverview(null); setAttribution(null); setEfficiency(null); setComparison(null)
  }, [datePreset])

  const reload = () => {
    if (subTab === 'overview') loadOverview()
    if (subTab === 'attribution') loadAttribution()
    // Reload manual da eficiência força re-coleta do gasto na Meta (resync=1).
    if (subTab === 'efficiency') loadEfficiency(true)
    if (subTab === 'comparison') loadComparison()
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Header: sub-tabs + filtro de período + reload */}
      <div className="shrink-0 px-4 py-3 border-b border-border bg-card/40 neu-bar flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <FiTrendingUp className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-semibold text-foreground">Meta Ads</span>
          {overview?.adAccount && (
            <span className="text-[10px] text-muted-foreground font-mono ml-1">{overview.adAccount}</span>
          )}
        </div>

        <div className="flex gap-0.5 bg-secondary/50 rounded-lg p-0.5">
          {([
            { key: 'overview' as const,    label: 'Visão geral',      icon: FiBarChart2 },
            { key: 'attribution' as const, label: 'Atribuição CRM',   icon: FiTarget },
            { key: 'efficiency' as const,  label: 'Eficiência',       icon: LuBanknote },
            { key: 'comparison' as const,  label: 'Comparação',       icon: FiUsers },
            { key: 'report' as const,      label: 'Relatório',        icon: FiFileText },
          ]).map(t => (
            <button key={t.key}
              onClick={() => setSubTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                subTab === t.key
                  ? 'bg-card text-foreground neu-relief'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              <t.icon className="w-3 h-3" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {subTab !== 'report' && (
            <select value={datePreset} onChange={e => setDatePreset(e.target.value as DatePreset)}
              className="text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500">
              <option value="last_7d">Últimos 7 dias</option>
              <option value="last_30d">Últimos 30 dias</option>
              <option value="last_90d">Últimos 90 dias</option>
            </select>
          )}
          <button onClick={reload}
            className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            title="Recarregar">
            <FiRefreshCw className={`w-3.5 h-3.5 ${overviewLoading || attributionLoading || efficiencyLoading || comparisonLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {subTab === 'overview' && <OverviewView data={overview} loading={overviewLoading} />}
        {subTab === 'attribution' && <AttributionView data={attribution} loading={attributionLoading} />}
        {subTab === 'efficiency' && <EfficiencyView data={efficiency} loading={efficiencyLoading} />}
        {subTab === 'comparison' && <ComparisonView data={comparison} loading={comparisonLoading} />}
        {subTab === 'report' && <ReportView />}
      </div>
    </div>
  )
}

// ─── Sub-view: Visão geral ───────────────────────────────────────────
function OverviewView({ data, loading }: { data: OverviewResponse | null; loading: boolean }) {
  if (loading && !data) return <Loading label="Buscando campanhas no Meta..." />
  if (!data) return null
  if (!data.success || data.error) return <ErrorState error={data.error || 'Erro desconhecido'} hint={data.hint} />

  const t = data.totals
  return (
    <div className="p-4 space-y-4">
      {/* KPIs globais do período */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Investido', value: fmtBRL(t.spend), color: '#10b981', icon: LuBanknote },
          { label: 'Impressões', value: fmtInt(t.impressions), color: '#3b82f6', icon: FiEye },
          { label: 'Cliques', value: fmtInt(t.clicks), color: '#8b5cf6', icon: FiTarget },
          { label: 'CTR', value: fmtPct(t.ctr), color: '#f59e0b', icon: FiBarChart2 },
          { label: 'Leads', value: fmtInt(t.leads), color: '#ec4899', icon: FiUsers },
          { label: 'CPL médio', value: t.cpl !== null ? fmtBRL(t.cpl) : '—', color: '#6366f1', icon: FiTrendingUp },
        ].map((kpi, i) => (
          <div key={i} className="bg-card neu-card rounded-xl border border-border p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <kpi.icon className="w-3 h-3" style={{ color: kpi.color }} />
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</span>
            </div>
            <div className="text-lg font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Tabela de campanhas */}
      <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <FiBarChart2 className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-foreground">Campanhas</h3>
          <span className="text-[10px] text-muted-foreground ml-auto">{data.campaigns.length} no período</span>
        </div>
        {data.campaigns.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma campanha com dados no período.</div>
        ) : (
          <div className="overflow-auto max-h-[600px]">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-card z-10 neu-thead">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Campanha</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Status</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Investido</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Impr.</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Cliques</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">CTR</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">CPC</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Leads</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">CPL</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c, i) => (
                  <tr key={c.id} className={`hover:bg-secondary/50 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                    <td className="px-3 py-1.5 text-foreground border-b border-border/30 font-medium max-w-[300px] truncate" title={c.name}>{c.name}</td>
                    <td className="px-3 py-1.5 border-b border-border/30">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                        c.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-500' :
                        c.status === 'PAUSED' ? 'bg-amber-500/15 text-amber-500' :
                        'bg-muted text-muted-foreground'
                      }`}>{c.status}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-semibold text-emerald-500 border-b border-border/30 tabular-nums">{fmtBRL(c.spend)}</td>
                    <td className="px-3 py-1.5 text-right text-foreground border-b border-border/30 tabular-nums">{fmtInt(c.impressions)}</td>
                    <td className="px-3 py-1.5 text-right text-foreground border-b border-border/30 tabular-nums">{fmtInt(c.clicks)}</td>
                    <td className="px-3 py-1.5 text-right text-muted-foreground border-b border-border/30 tabular-nums">{fmtPct(c.ctr)}</td>
                    <td className="px-3 py-1.5 text-right text-muted-foreground border-b border-border/30 tabular-nums">{fmtBRL(c.cpc)}</td>
                    <td className="px-3 py-1.5 text-right text-pink-500 font-semibold border-b border-border/30 tabular-nums">{c.leads || '-'}</td>
                    <td className="px-3 py-1.5 text-right text-indigo-500 font-semibold border-b border-border/30 tabular-nums">{c.cpl !== null ? fmtBRL(c.cpl) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Sub-view: Atribuição CRM ─────────────────────────────────────────
function AttributionView({ data, loading }: { data: AttributionResponse | null; loading: boolean }) {
  if (loading && !data) return <Loading label="Cruzando leads com anúncios..." />
  if (!data) return null
  if (!data.success || data.error) return <ErrorState error={data.error || 'Erro desconhecido'} />

  if (data.ads.length === 0) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-blue-500/5 border border-blue-500/30 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <FiInfo className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">Sem leads atribuídos ainda</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {data.note || 'Nenhum lead chegou via Click-to-WhatsApp no período.'}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                <strong>Como funciona:</strong> quando o lead clica num anúncio Meta com botão "Enviar mensagem",
                a primeira msg dele vem com um <span className="font-mono text-foreground/80">referral.source_id</span>{' '}
                identificando o anúncio. O parser está ativo desde 2026-05-18 — leads de antes não são
                rastreáveis.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <FiTarget className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-foreground">Atribuição lead → anúncio</h3>
          <span className="text-[10px] text-muted-foreground ml-auto">{data.ads.length} anúncios geraram leads</span>
        </div>
        <div className="overflow-auto max-h-[600px]">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-card z-10 neu-thead">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Anúncio</th>
                <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Campanha</th>
                <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Leads</th>
                <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Investido</th>
                <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">CPL real</th>
                <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Período</th>
              </tr>
            </thead>
            <tbody>
              {data.ads.map((a, i) => (
                <tr key={a.adId} className={`hover:bg-secondary/50 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                  <td className="px-3 py-1.5 text-foreground border-b border-border/30 font-medium max-w-[300px] truncate" title={a.adName || a.adId}>
                    {a.adName || <span className="text-muted-foreground italic">Anúncio {a.adId} (não encontrado)</span>}
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30 max-w-[200px] truncate" title={a.campaign?.name || '-'}>
                    {a.campaign?.name || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right text-pink-500 font-semibold border-b border-border/30 tabular-nums">{a.leads}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-500 font-semibold border-b border-border/30 tabular-nums">
                    {a.spend !== null ? fmtBRL(a.spend) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right text-indigo-500 font-semibold border-b border-border/30 tabular-nums">
                    {a.cpl !== null ? fmtBRL(a.cpl) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-[10px] text-muted-foreground border-b border-border/30">
                    {new Date(a.firstLead).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} →{' '}
                    {new Date(a.lastLead).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ─── Sub-view: Eficiência (custo × conversão por criativo) ───────────
// Frente 1 do roadmap Pareto: gasto real (cache diário coletado da Meta) cruzado
// com leads CTWA e conversões confirmadas (CPF no Oracle). Fadiga = 50+ leads e
// 0 conversões em 21d com gasto ativo.
function EfficiencyView({ data, loading }: { data: EfficiencyResponse | null; loading: boolean }) {
  if (loading && !data) return <Loading label="Cruzando gasto, leads e conversões..." />
  if (!data) return null
  if (!data.success || data.error) return <ErrorState error={data.error || 'Erro desconhecido'} />

  const fatigued = data.ads.filter(a => a.fatigued)
  const t = data.totals
  const fmtDate = (s: string | null) => s ? new Date(`${s}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'

  return (
    <div className="p-4 space-y-4">
      {/* KPIs do recorte */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: 'Investido', value: fmtBRL(t.spend), color: '#10b981', icon: LuBanknote, hint: 'Gasto somado dos anúncios no período (cache diário coletado da Meta)' },
          { label: 'Leads CTWA', value: fmtInt(t.leads), color: '#ec4899', icon: FiUsers, hint: 'Conversas iniciadas por clique em anúncio (referral rastreado)' },
          { label: 'Conversões', value: fmtInt(t.conversions), color: '#8b5cf6', icon: FiTrendingUp, hint: 'Leads que viraram consultora (CPF confirmado no Oracle)' },
          { label: 'CPL', value: t.cpl !== null ? fmtBRL(t.cpl) : '—', color: '#6366f1', icon: FiTarget, hint: 'Custo por lead: investido ÷ leads' },
          { label: 'Custo/Conversão', value: t.costPerConversion !== null ? fmtBRL(t.costPerConversion) : '—', color: '#f59e0b', icon: FiBarChart2, hint: 'Investido ÷ conversões confirmadas — o número que importa' },
        ].map((kpi, i) => (
          <div key={i} className="bg-card neu-card rounded-xl border border-border p-3" title={kpi.hint}>
            <div className="flex items-center gap-1.5 mb-1">
              <kpi.icon className="w-3 h-3" style={{ color: kpi.color }} />
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</span>
            </div>
            <div className="text-lg font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Alerta de criativos fadigados */}
      {fatigued.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">
                {fatigued.length === 1 ? 'Criativo fadigado' : `${fatigued.length} criativos fadigados`}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Com gasto ativo, {data.fatigueRule.minLeads}+ leads e <strong>zero conversões</strong> nos
                últimos {data.fatigueRule.windowDays} dias — trazem volume que não vira consultora.
                Candidatos a pausar ou trocar o criativo:
              </p>
              <ul className="mt-2 space-y-0.5">
                {fatigued.map(a => (
                  <li key={a.adId} className="text-xs text-foreground">
                    <span className="font-medium">{a.adName || a.adId}</span>
                    <span className="text-muted-foreground"> — {fmtBRL(a.spendRecent)} gastos e {a.leadsRecent} leads nos últimos {data.fatigueRule.windowDays}d, 0 conversões</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tabela por anúncio */}
      <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <LuBanknote className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-foreground">Custo × conversão por anúncio</h3>
          <span className="text-[10px] text-muted-foreground ml-auto" title="Recorte de datas do gasto e dos leads">
            {fmtDate(data.since)} → {fmtDate(data.until)} · {data.ads.length} anúncios
          </span>
        </div>
        {data.ads.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">{data.note || 'Sem dados no período.'}</div>
        ) : (
          <div className="overflow-auto max-h-[600px]">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-card z-10 neu-thead">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Anúncio</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Campanha</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Investido</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Leads</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">CPL</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Conv.</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border" title="Investido ÷ conversões confirmadas">R$/Conv.</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border" title="Último dia com gasto registrado">Ativo até</th>
                </tr>
              </thead>
              <tbody>
                {data.ads.map((a, i) => (
                  <tr key={a.adId} className={`hover:bg-secondary/50 ${a.fatigued ? 'bg-amber-500/5' : i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                    <td className="px-3 py-1.5 text-foreground border-b border-border/30 font-medium max-w-[280px] truncate" title={a.adName || a.adId}>
                      {a.fatigued && <FiAlertCircle className="w-3 h-3 text-amber-500 inline mr-1 -mt-0.5" title="Criativo fadigado: muitos leads, zero conversões" />}
                      {a.adName || <span className="text-muted-foreground italic font-mono">{a.adId}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30 max-w-[180px] truncate" title={a.campaignName || '-'}>{a.campaignName || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-semibold text-emerald-500 border-b border-border/30 tabular-nums">{a.spend > 0 ? fmtBRL(a.spend) : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-pink-500 font-semibold border-b border-border/30 tabular-nums">{a.leads || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-indigo-500 border-b border-border/30 tabular-nums">{a.cpl !== null ? fmtBRL(a.cpl) : '—'}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold border-b border-border/30 tabular-nums ${a.conversions > 0 ? 'text-violet-500' : 'text-muted-foreground/50'}`}>{a.conversions || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-amber-500 font-semibold border-b border-border/30 tabular-nums">{a.costPerConversion !== null ? fmtBRL(a.costPerConversion) : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-[10px] text-muted-foreground border-b border-border/30 tabular-nums">{fmtDate(a.lastActive)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2 text-[10px] text-muted-foreground border-t border-border bg-secondary/30">
          Anúncio com gasto e sem leads = campanha sem Click-to-WhatsApp ou criativo que não gera conversa.
          Conversão = lead virou consultora (CPF no Oracle), atribuída ao anúncio do 1º contato.
          {data.lastSyncAt && <> Gasto sincronizado da Meta em {new Date(data.lastSyncAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.</>}
        </div>
      </div>
    </div>
  )
}

// ─── Sub-view: Comparação Meta × WhatsApp ─────────────────────────────
function ComparisonView({ data, loading }: { data: ComparisonResponse | null; loading: boolean }) {
  if (loading && !data) return <Loading label="Cruzando funil completo..." />
  if (!data) return null
  if (!data.success || data.error) return <ErrorState error={data.error || 'Erro desconhecido'} />

  const adIds = Object.keys(data.matrix)
  if (adIds.length === 0) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-blue-500/5 border border-blue-500/30 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <FiInfo className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">Sem dados pra comparar</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {data.note || 'Nenhum lead CTWA recebeu disparo de template WhatsApp no período.'}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                <strong>Como funciona:</strong> cruza cada lead que veio de um anúncio Meta com os templates
                WhatsApp que foram disparados pra ele depois. Mostra qual fluxo do funil cada anúncio
                alimenta — útil pra avaliar se o anúncio X traz leads que recebem template Y mais
                frequentemente.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Coleta lista única de templates pra montar headers da tabela
  const allTemplates = new Set<string>()
  for (const adId of adIds) {
    for (const t of Object.keys(data.matrix[adId].templates)) allTemplates.add(t)
  }
  const templateList = Array.from(allTemplates).sort()

  return (
    <div className="p-4">
      <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <FiUsers className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-foreground">Anúncio Meta × Template WhatsApp</h3>
          <span className="text-[10px] text-muted-foreground ml-auto">{adIds.length} anúncios × {templateList.length} templates</span>
        </div>
        <div className="overflow-auto max-h-[600px]">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-card z-10 neu-thead">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border sticky left-0 bg-card neu-frozen">Anúncio</th>
                <th className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border">Total leads</th>
                {templateList.map(t => (
                  <th key={t} className="px-3 py-2 text-right font-semibold text-muted-foreground border-b border-border max-w-[120px] truncate" title={t}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adIds.map((adId, i) => {
                const ad = data.matrix[adId]
                return (
                  <tr key={adId} className={`hover:bg-secondary/50 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                    <td className="px-3 py-1.5 text-foreground border-b border-border/30 font-medium font-mono sticky left-0 bg-inherit neu-frozen">{adId}</td>
                    <td className="px-3 py-1.5 text-right text-pink-500 font-semibold border-b border-border/30 tabular-nums">{ad._totalLeads}</td>
                    {templateList.map(t => {
                      const cell = ad.templates[t]
                      if (!cell) return <td key={t} className="px-3 py-1.5 text-center text-muted-foreground/30 border-b border-border/30">—</td>
                      const total = cell.sent + cell.delivered + cell.read + cell.failed
                      return (
                        <td key={t} className="px-3 py-1.5 text-right border-b border-border/30 tabular-nums">
                          <span className="font-semibold text-foreground">{total}</span>
                          {cell.read > 0 && <span className="text-[9px] text-violet-500 ml-1">{cell.read}L</span>}
                          {cell.failed > 0 && <span className="text-[9px] text-red-500 ml-1">{cell.failed}F</span>}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[10px] text-muted-foreground border-t border-border bg-secondary/30">
          Legenda: número = total disparado | <span className="text-violet-500">XL</span> = leitura | <span className="text-red-500">XF</span> = falhas
        </div>
      </div>
    </div>
  )
}

// ─── Auxiliares ───────────────────────────────────────────────────────
function Loading({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
      <FiLoader className="w-6 h-6 animate-spin text-blue-500" />
      <span className="text-sm">{label}</span>
    </div>
  )
}

function ErrorState({ error, hint }: { error: string; hint?: string }) {
  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-foreground mb-1">Erro ao consultar Meta Ads</h3>
            <p className="text-xs text-muted-foreground leading-relaxed font-mono break-all">{error}</p>
            {hint && (
              <p className="text-xs text-amber-500 mt-2 flex items-center gap-1">
                <FiExternalLink className="w-3 h-3" /> {hint}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
